import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Mail, Eye, EyeOff, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { Aurora, ParticleField } from "../components/Background";
import useSEO from "../hooks/useSEO";

function formatApiError(err) {
  const d = err?.response?.data?.detail;
  if (!d) return err?.message || "Something went wrong.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(d);
}

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [busy, setBusy] = useState(false);

  useSEO({
    title: "Admin Sign In · DietDotIn",
    description: "Private admin sign-in for DietDotIn lead management.",
    noindex: true,
  });

  if (user && user.role === "admin") {
    return <Navigate to="/admin/leads" replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      toast.error("Email and password are required.");
      return;
    }
    setBusy(true);
    try {
      await login(email.trim(), password);
      toast.success("Welcome back");
      navigate("/admin/leads");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center px-5 py-16">
      <Aurora />
      <ParticleField count={18} />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative w-full max-w-md glass-strong rounded-3xl p-8 md:p-10"
        data-testid="admin-login-card"
      >
        <a href="/" className="inline-flex items-center gap-3 mb-8">
          <img
            src="/brand/logo.png?v=4"
            alt="DietDotIn"
            className="h-14 w-auto drop-shadow-[0_0_20px_rgba(122,43,255,0.55)]"
          />
        </a>

        <h1 className="font-display font-extrabold text-3xl md:text-4xl text-white">
          Admin <span className="gradient-text">Sign In</span>
        </h1>
        <p className="mt-3 text-[#D8CCFF] text-sm font-body">
          Manage consultation enquiries and track lead conversion.
        </p>

        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="admin-login-form">
          <div>
            <label className="text-[#CFAEFF] text-xs font-heading uppercase tracking-wider">
              Email
            </label>
            <div className="relative mt-2">
              <Mail
                size={16}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-[#CFAEFF]"
              />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@dietdotin.com"
                className="field w-full pl-11 pr-4 py-3 rounded-xl text-sm md:text-base"
                data-testid="admin-email"
                autoComplete="email"
              />
            </div>
          </div>

          <div>
            <label className="text-[#CFAEFF] text-xs font-heading uppercase tracking-wider">
              Password
            </label>
            <div className="relative mt-2">
              <Lock
                size={16}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-[#CFAEFF]"
              />
              <input
                type={showPwd ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••••"
                className="field w-full pl-11 pr-12 py-3 rounded-xl text-sm md:text-base"
                data-testid="admin-password"
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPwd((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#CFAEFF] hover:text-white p-1"
                data-testid="toggle-password"
                aria-label="Toggle password visibility"
              >
                {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={busy}
            className="btn-primary w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full font-heading font-semibold disabled:opacity-60"
            data-testid="admin-login-submit"
          >
            {busy ? "Signing in…" : "Sign In"}
            {!busy && <ArrowRight size={16} />}
          </button>
        </form>

        <a
          href="/"
          className="mt-6 inline-block text-[#CFAEFF] text-xs hover:text-white"
        >
          ← Back to site
        </a>
      </motion.div>
    </div>
  );
}
